/**
 * ConditionBuilder — the optional show_if branching editor (WCR parity, blue
 * box). One condition per question: pick a source question, an operator
 * (type-aware), and a value. Calculation questions are excluded as sources —
 * they resolve only at completion, so branching on one is a dead branch the
 * backend rejects at publish.
 */
import { X, Plus } from 'lucide-react';
import { FormQuestionDef, FormShowIf } from '../../types';
import { OPERATOR_LABELS, showIfOperatorsForType } from './formMeta';

interface ConditionBuilderProps {
  condition: FormShowIf | null | undefined;
  /** Candidate source questions (caller excludes self + calculations). */
  questions: FormQuestionDef[];
  onChange: (condition: FormShowIf | null) => void;
}

const selectCls =
  'block w-full rounded-lg border border-primary-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20 dark:border-primary-700 dark:bg-gray-800 dark:text-gray-100';

export default function ConditionBuilder({ condition, questions, onChange }: ConditionBuilderProps) {
  const selected = questions.find((q) => q.key === condition?.question_key);

  const handleQuestionChange = (key: string) => {
    const q = questions.find((x) => x.key === key);
    onChange({ question_key: key, operator: 'equals', value: q?.type === 'yes_no' ? 'yes' : '' });
  };

  const renderValueInput = () => {
    if (!selected || !condition) return null;
    const options = selected.options || [];
    if (options.length > 0) {
      return (
        <select value={String(condition.value ?? '')} onChange={(e) => onChange({ ...condition, value: e.target.value })} className={selectCls}>
          <option value="">Select a value</option>
          {options.map((o, i) => <option key={`${i}:${o.value}`} value={o.value}>{o.label}</option>)}
        </select>
      );
    }
    if (selected.type === 'yes_no') {
      return (
        <select value={String(condition.value ?? '')} onChange={(e) => onChange({ ...condition, value: e.target.value })} className={selectCls}>
          <option value="">Select a value</option>
          <option value="yes">Yes</option>
          <option value="no">No</option>
        </select>
      );
    }
    if (selected.type === 'number' || selected.type === 'scale') {
      return (
        <input
          type="number"
          value={String(condition.value ?? '')}
          onChange={(e) => onChange({ ...condition, value: e.target.value === '' ? '' : Number(e.target.value) })}
          placeholder="Enter a number"
          className={selectCls}
        />
      );
    }
    return (
      <input type="text" value={String(condition.value ?? '')} onChange={(e) => onChange({ ...condition, value: e.target.value })} placeholder="Enter a value" className={selectCls} />
    );
  };

  if (questions.length === 0) {
    return <p className="text-sm text-gray-500 dark:text-gray-400">Add another non-calculation question first to enable conditional display.</p>;
  }

  if (!condition) {
    return (
      <button
        type="button"
        onClick={() => handleQuestionChange(questions[0].key)}
        className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-600 transition-colors duration-150 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
      >
        <Plus className="h-4 w-4" /> Add condition
      </button>
    );
  }

  return (
    <div className="rounded-lg border border-primary-200 bg-primary-50 p-4 dark:border-primary-800 dark:bg-primary-900/20">
      <div className="mb-3 flex items-center justify-between">
        <span className="text-sm font-medium text-primary-700 dark:text-primary-300">Show this question when</span>
        <button type="button" onClick={() => onChange(null)} className="rounded p-1 text-primary-600 hover:bg-primary-100 dark:text-primary-300 dark:hover:bg-primary-900/40" aria-label="Remove condition">
          <X className="h-4 w-4" />
        </button>
      </div>
      <div className="space-y-3">
        <div>
          <label className="mb-1 block text-xs font-medium text-primary-700 dark:text-primary-300">Question</label>
          <select value={condition.question_key} onChange={(e) => handleQuestionChange(e.target.value)} className={selectCls}>
            {questions.map((q) => (
              <option key={q.key} value={q.key}>{(q.label || q.key).length > 60 ? `${(q.label || q.key).slice(0, 60)}…` : (q.label || q.key)}</option>
            ))}
          </select>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select
            value={condition.operator}
            onChange={(e) => onChange({ ...condition, operator: e.target.value })}
            className={`${selectCls} w-auto`}
          >
            {showIfOperatorsForType(selected?.type).map((op) => <option key={op} value={op}>{OPERATOR_LABELS[op]}</option>)}
          </select>
          <div className="min-w-[150px] flex-1">{renderValueInput()}</div>
        </div>
      </div>
    </div>
  );
}
